import { deleteCookie, getCookie, setCookie } from "@/shared/utils/cookies";
import type { WizardData } from "../../authentication/signup/types";
import { COOKIE_KEYS, WIZARD_DATA_KEYS } from "../constants";

/**
 * Extracts and cleans up UTM data from cookies
 */
export const getUtmData = (): Record<string, string> | null => {
  const payload: Record<string, string> = {};
  let hasData = false;

  const cookies = document.cookie
    .split(";")
    .filter((cookie) => cookie.indexOf(COOKIE_KEYS.UTM_PREFIX) > -1);

  for (const cookie of cookies) {
    const [k, v] = cookie.trim().split("=");
    if (k && v) {
      payload[k.replace(COOKIE_KEYS.UTM_PREFIX, "last touchpoint utm")] = v;
      deleteCookie(k);
      hasData = true;
    }
  }

  return hasData ? payload : null;
};

/**
 * Gets wizard data from cookie
 */
export const getWebsiteWizardData = (): WizardData => {
  const data = getCookie(COOKIE_KEYS.WIZARD_COOKIE_KEY);
  if (!data) return {};

  try {
    const parsed = JSON.parse(data);
    const payload: WizardData = {};

    for (const key of WIZARD_DATA_KEYS) {
      if (parsed[key]) {
        (payload as Record<string, string>)[key] = parsed[key] as string;
      }
    }

    return payload;
  } catch (e) {
    console.error("Error parsing wizard data:", e);
    return {};
  }
};

/**
 * Generates or retrieves testing number for A/B testing
 */
export const getTestingNumber = (): number => {
  let testingNumber = getCookie(COOKIE_KEYS.TESTING_NUMBER);
  if (!testingNumber) {
    testingNumber = (Math.floor(Math.random() * 12) + 1).toString();
    setCookie({
      name: COOKIE_KEYS.TESTING_NUMBER,
      value: testingNumber,
      days: 365,
    });
  }
  return Number(testingNumber);
};

/**
 * Extracts query parameters from URL
 */
export const extractQueryParameters = (url: string): Record<string, string> => {
  const queryString = url.split("?")[1];
  if (!queryString) return {};

  const params = new URLSearchParams(queryString);
  const data: Record<string, string> = {};

  for (const [key, value] of Array.from(params.entries())) {
    data[key] = decodeURIComponent(value.replace(/\+/g, " "));
  }

  return data;
};

/**
 * Gets landing page pathname by removing domain
 */
export const getLandingPagePathname = (landingPage: string): string => {
  let pathname = landingPage || "";

  const domainToRemove = "storydoc.com";
  const idx = pathname.indexOf(domainToRemove);
  if (idx !== -1) {
    pathname = pathname.substring(idx + domainToRemove.length);
  }

  return pathname;
};
